import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';

interface State { id: number; name: string }
interface City { id: number; name: string }

interface Props { pincodeId?: number; defaultValues?: any; onSuccess: () => void }

export function PincodeForm({ pincodeId, defaultValues, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!pincodeId;
  const { data: states } = useQuery({ queryKey: ['states'], queryFn: async () => (await api.get<State[]>('/states')).data });
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      isActive: true, isMetro: false, isSpecDest: false, isForward: true, isPickup: true, isReverse: false,
      ...defaultValues,
      stateId: defaultValues?.stateId ?? '',
      cityId: defaultValues?.cityId ?? '',
    },
  });
  const watchedStateId = watch('stateId');
  const { data: cities } = useQuery({
    queryKey: ['cities', 'by-state', watchedStateId],
    queryFn: async () => (await api.get<{ items: City[] }>('/cities', { params: { stateId: watchedStateId, pageSize: 200 } })).data.items,
    enabled: !!watchedStateId,
  });
  const save = useMutation({
    mutationFn: async (d: any) => {
      const payload: any = { ...d };
      ['stateId','cityId'].forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; else payload[k] = Number(payload[k]); });
      if (payload.pincode) payload.pincode = Number(payload.pincode);
      return isEdit
        ? (await api.patch(`/pincodes/${pincodeId}`, payload)).data
        : (await api.post('/pincodes', payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pincodes'] }); toast.success(isEdit ? 'Updated' : 'Created'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Pincode *" error={(errors as any).pincode?.message}>
          <input type="number" {...register('pincode', { required: 'Required', min: 100000, max: 999999 })} className="input font-mono" />
        </Field>
        <Field label="Zone">
          <select {...register('zone')} className="input font-mono">
            <option value="">—</option>
            {['C','C1','E','E1','N','N1','N2','NE','NE2','S','S1','S2','SP1','SP2'].map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
        <Field label="State">
          <select {...register('stateId')} className="input">
            <option value="">—</option>
            {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="City">
          <select {...register('cityId')} className="input">
            <option value="">—</option>
            {cities?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Billing Zone">
          <input {...register('billingZone')} className="input font-mono uppercase" placeholder="N1, E, S2..." />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm text-slate-700 pt-1">
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isMetro')} className="rounded" /> Metro</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isSpecDest')} className="rounded" /> Special Dest</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isForward')} className="rounded" /> Forward</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isPickup')} className="rounded" /> Pickup</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isReverse')} className="rounded" /> Reverse</label>
        <label className="flex items-center gap-2"><input type="checkbox" {...register('isActive')} className="rounded" /> Active</label>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button type="button" onClick={onSuccess} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={save.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-lg disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Save' : 'Create'}
        </button>
      </div>
    </form>
  );
}
function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
